import { lazy, Suspense, useEffect, useState, useTransition } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router';
import MainWrapper from '@/containers/App/MainWrapper';
import styled from 'styled-components';
import { colorBackgroundBody } from '@/utils/palette';
import { paddingLeft } from '@/utils/directions';
import Layout from '@/containers/Layout';
import Loading from '@/shared/components/Loading';
import useAuthState from '@/hooks/use-auth-state';
import { firebaseService } from '@/services/firebase';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { setInStorage } from '@/services/storage';
import { ACCESS_TOKEN } from '@/constants';
import { getUserProfile } from '@/services/users';
import { useAppDispatch } from '@/hooks/redux';
import { setProfile } from '@/stores/reducers/userReducer';
import Login from '@/containers/Account/Login';
import EmailConfirmation from '@/containers/Account/EmailConfimation';
import Categories from '@/containers/Categories';
import Contents from '@/containers/Contents';
import Courses from '@/containers/Courses';
import Blog from '@/containers/Blog';
import Products from '@/containers/Products';
import Orders from '@/containers/Orders';
import CourseCrm from '@/containers/CourseCrm';
import Cohorts from '@/containers/Cohorts';
import Events from '@/containers/Events';
import LiveSessions from '@/containers/LiveSessions';
import EventRegistrations from '@/containers/EventRegistrations';
import ContentReports from '@/containers/ContentReports';
import Course33Videos from '@/containers/Course33Videos';
import Course33Day0Videos from '@/containers/Course33Media/day0-videos';
import Course33DayMedia from '@/containers/Course33Media/day-media';
import MeditationRooms from '@/containers/MeditationRooms';
import AstroTips from '@/containers/AstroTips';
import Settings from '@/containers/Settings';
import Schedules from '@/containers/Schedules';
import ScheduleDetail from '@/containers/Schedules/detail';
import Users from '@/containers/Users';
import UserProfile from '@/containers/Users/profile';
import TelegramBotStats from '@/containers/TelegramBotStats';
import Referrals from '@/containers/Referrals';
import Profile from '@/containers/Account/Profile';
import NotFound from '@/containers/DefaultPage/404';

export const AUTH_ROUTES = [
    { path: '/login', element: Login },
    { path: '/email-confirmation', element: EmailConfirmation },
];

const Commerce = lazy(() => import('@/containers/App/Router/Commerce'));
const OnLineMarketingDashboard = lazy(() => import('@/containers/Dashboards/OnLineMarketing'));
const AppDashboard = lazy(() => import('@/containers/Dashboards/App'));
const BookingDashboard = lazy(() => import('@/containers/Dashboards/Booking'));
const FitnessDashboard = lazy(() => import('@/containers/Dashboards/Fitness'));
const UI = lazy(() => import('@/containers/App/Router/UI'));
const Mail = lazy(() => import('@/containers/Mail'));
const Todo = lazy(() => import('@/containers/Todo'));
const Forms = lazy(() => import('@/containers/App/Router/Forms'));
const Tables = lazy(() => import('@/containers/App/Router/Tables'));
const Charts = lazy(() => import('@/containers/App/Router/Charts'));
const Maps = lazy(() => import('@/containers/App/Router/Maps'));
const ECommerce = lazy(() => import('@/containers/App/Router/ECommerce'));
const DefaultPages = lazy(() => import('@/containers/App/Router/DefaultPages'));
const ConnectWallet = lazy(() => import('@/containers/Dashboards/Wallet/ConnectWallet'));
const FinanceDashboard = lazy(() => import('@/containers/Dashboards/Finance'));

export const USER_ROUTES = [
    { path: '/users', element: Users },
    { path: '/users/:userId', element: UserProfile },
    { path: '/telegram-bot-stats', element: TelegramBotStats },
    { path: '/categories', element: Categories },
    { path: '/contents', element: Contents },
    { path: '/courses', element: Courses },
    { path: '/blog', element: Blog },
    { path: '/products', element: Products },
    { path: '/orders', element: Orders },
    { path: '/course-crm', element: CourseCrm },
    { path: '/cohorts', element: Cohorts },
    { path: '/events', element: Events },
    { path: '/live-sessions', element: LiveSessions },
    { path: '/event-registrations', element: EventRegistrations },
    { path: '/content-reports', element: ContentReports },
    { path: '/course-33-videos', element: Course33Videos },
    { path: '/course-33-day0-videos', element: Course33Day0Videos },
    { path: '/course-33-day-media', element: Course33DayMedia },
    { path: '/meditation-rooms', element: MeditationRooms },
    { path: '/astro-tips', element: AstroTips },
    { path: '/settings', element: Settings },
    { path: '/schedules', element: Schedules },
    { path: '/schedules/:scheduleId', element: ScheduleDetail },
    { path: '/referrals', element: Referrals },
    { path: '/profile', element: Profile },
    { path: '/e_commerce_dashboard', element: Commerce },
    { path: '/online_marketing_dashboard', element: OnLineMarketingDashboard },
    { path: '/connect_wallet', element: ConnectWallet },
    { path: '/app_dashboard', element: AppDashboard },
    { path: '/booking_dashboard', element: BookingDashboard },
    { path: '/finance_dashboard', element: FinanceDashboard },
    { path: '/fitness_dashboard', element: FitnessDashboard },
    { path: '/ui', element: UI },
    { path: '/mail', element: Mail },
    { path: '/todo', element: Todo },
    { path: '/forms', element: Forms },
    { path: '/tables', element: Tables },
    { path: '/charts', element: Charts },
    { path: '/maps', element: Maps },
    { path: '/e-commerce', element: ECommerce },
    { path: '/default_pages', element: DefaultPages },
];

const ContainerWrap = styled.div`
    padding-top: 90px;
    min-height: 100vh;
    transition: padding-left 0.3s;

    ${paddingLeft}: 0;

    background: ${colorBackgroundBody};

    @media screen and (min-width: 576px) {
        ${paddingLeft}: 250px;
    }

    @media screen and (max-width: 576px) {
        padding-top: 150px;
    }
`;

const Router = () => {
    const [firebaseAuthUser, loading] = useAuthState(firebaseService.getAuth());
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const [, startTransition] = useTransition();

    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const completeSignIn = async (email: string) => {
        const firebaseAuth = firebaseService.getAuth();
        if (isSignInWithEmailLink(firebaseAuth, window.location.href)) {
            try {
                await signInWithEmailLink(firebaseAuth, email, window.location.href);
            } catch (error) {
                navigate(`/login?redirectTo=/users`, { replace: true });
                startTransition(() => setIsLoaded(true));
            }
        } else {
            navigate(`/login?redirectTo=/users`, { replace: true });
            startTransition(() => setIsLoaded(true));
        }
    };

    useEffect(() => {
        const email = searchParams.get('email');

        if (!firebaseAuthUser && !loading && email) {
            completeSignIn(email);
        } else if (!firebaseAuthUser && !loading) {
            navigate(`/login?redirectTo=/users`, { replace: true });
            startTransition(() => setIsLoaded(true));
        }

        if (firebaseAuthUser) {
            firebaseAuthUser.getIdToken().then((idToken) => {
                setInStorage(ACCESS_TOKEN, idToken);
                setAccessToken(idToken);
            });
        }
    }, [firebaseAuthUser, loading]);

    const getUserProfileAndRedirect = async () => {
        if (firebaseAuthUser?.uid && accessToken) {
            const { data: userProfile } = await getUserProfile(firebaseAuthUser.uid, accessToken);
            if (userProfile?.user) {
                dispatch(setProfile(userProfile.user));

                if (['/', ...AUTH_ROUTES.flatMap((r) => r.path)].includes(pathname)) {
                    return await navigate('/users', { replace: true });
                }
            }
        }
    };

    useEffect(() => {
        if (accessToken) {
            getUserProfileAndRedirect();
            startTransition(() => setIsLoaded(true));

            if (['/', ...AUTH_ROUTES.flatMap((r) => r.path)].includes(pathname)) {
                navigate('/users', { replace: true });
            }
        }
    }, [accessToken]);

    return (
        <MainWrapper>
            <main>
                <Suspense fallback={<Loading loading={loading || !isLoaded} />}>
                    {loading ? (
                        <Loading loading={loading || !isLoaded} />
                    ) : !firebaseAuthUser ? (
                        <Routes>
                            {AUTH_ROUTES.map((route) => (
                                <Route key={route.path} path={route.path} element={<route.element />} />
                            ))}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    ) : (
                        <div>
                            <Layout />
                            <ContainerWrap>
                                <Routes>
                                    {USER_ROUTES.map((route) => (
                                        <Route key={route.path} path={route.path} element={<route.element />} />
                                    ))}
                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </ContainerWrap>
                        </div>
                    )}
                </Suspense>
            </main>
        </MainWrapper>
    );
};

export default Router;
